import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, MenuItem, Snackbar, TextField } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CleaningServicesRoundedIcon from "@mui/icons-material/CleaningServicesRounded";
import { useSnackbar } from "../../hooks/useSnackbar";
import { Badge, Panel, ListGrid, PrimaryButton, GhostButton } from "./ui";
import DateRangePresetButtons from "../../components/DateRangePresetButtons";
import { getDateRangePreset } from "../../utils/dateRangePresets";
import { formatEpoch } from "../../utils/dateUtils";
import {
  formatKurus,
  TRANSACTION_TYPES,
  TRANSACTION_TYPE,
  BALANCE_ISG,
  BALANCE_STANDARD,
  transactionTypeLabel,
  balanceTypeLabel,
} from "./constants";
import { getTransactionsByFilter, getPersonal } from "../../api/digitalPayAPI";
import {
  createAndDownloadExcel,
  generateExcelFilename,
} from "../../utils/excelExportUtils";
import { isSuperAdminUser } from "./roleUtils";

const balanceTypeTone = (value) => {
  if (Number(value) === BALANCE_ISG) return "purple";
  if (Number(value) === BALANCE_STANDARD) return "primary";
  return "neutral";
};

const transactionTypeTone = (value) => {
  const transactionType = Number(value);
  if (transactionType === TRANSACTION_TYPE.PAYMENT) return "success";
  if (transactionType === TRANSACTION_TYPE.REFUND) return "warning";
  if (
    transactionType === TRANSACTION_TYPE.LOAD_ROLLOVER_BALANCE ||
    transactionType === TRANSACTION_TYPE.LOAD_NO_ROLLOVER_BALANCE
  ) {
    return "primary";
  }
  return "neutral";
};

const toEpoch = (date, endOfDay = false) => {
  if (!date) return 0;
  const time = endOfDay ? "23:59:59" : "00:00:00";
  return Math.floor(new Date(`${date}T${time}`).getTime() / 1000);
};

const toFilterRequest = (filter) => {
  const request = { count: 0 };
  const startEpoch = toEpoch(filter.start);
  const endEpoch = toEpoch(filter.end, true);
  const personalId = Number(filter.personalId);
  const cardNo = String(filter.cardNo || "").trim();
  const departmentName = String(filter.departmentName || "").trim();

  if (startEpoch > 0) request.startEpoch = startEpoch;
  if (endEpoch > 0) request.endEpoch = endEpoch;
  if (personalId > 0) request.personalId = personalId;
  if (cardNo) request.cardNo = cardNo;
  if (departmentName) request.departmentName = departmentName;
  if (filter.type !== "") request.transactionTypeId = Number(filter.type);
  return request;
};

// İşlem geçmişindeki bütün sorgular backend filtre endpointine POST edilir.
export default function TransactionsPanel({ lookups }) {
  const { firms = [], departments = [] } = lookups;
  const canSelectFirm = isSuperAdminUser();
  const assignedFirmName = canSelectFirm ? "" : (firms[0]?.name ?? "");
  const [allRows, setAllRows] = useState([]);
  const [appliedFirm, setAppliedFirm] = useState(assignedFirmName);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const [draft, setDraft] = useState({
    start: "",
    end: "",
    firm: assignedFirmName,
    type: "",
    personalId: "",
    cardNo: "",
    departmentName: "",
  });

  const load = useCallback(async (filter) => {
    setLoading(true);
    try {
      setAllRows(await getTransactionsByFilter(toFilterRequest(filter)));
      setAppliedFirm(filter.firm || "");
    } catch (e) {
      showSnackbar(e.message || "İşlemler alınamadı", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    load({
      start: "",
      end: "",
      firm: assignedFirmName,
      type: "",
      personalId: "",
      cardNo: "",
      departmentName: "",
    });
  }, [assignedFirmName, load]);

  useEffect(() => {
    if (!canSelectFirm) {
      setDraft((current) => ({ ...current, firm: assignedFirmName }));
      setAppliedFirm(assignedFirmName);
    }
  }, [assignedFirmName, canSelectFirm]);

  useEffect(() => {
    getPersonal()
      .then(setPeople)
      .catch(() => setPeople([]));
  }, []);

  const departmentById = useMemo(
    () => new Map(departments.map((d) => [Number(d.iddepartment), d])),
    [departments]
  );

  const personOptions = useMemo(
    () =>
      people.filter((person) => {
        if (!draft.departmentName) return true;
        return (
          departmentById.get(Number(person.department_iddepartment))?.name ===
          draft.departmentName
        );
      }),
    [departmentById, draft.departmentName, people]
  );

  const rows = useMemo(() => {
    if (!appliedFirm) return allRows;
    return allRows.filter((row) => row.firmName === appliedFirm);
  }, [allRows, appliedFirm]);

  const departmentOptions = useMemo(
    () =>
      departments.filter(
        (department) =>
          !draft.firm || Number(department.firm_idfirm) === Number(
            firms.find((firm) => firm.name === draft.firm)?.idfirm
          )
      ),
    [departments, draft.firm, firms]
  );

  const apply = () => load(draft);

  const clearFilters = () => {
    const cleared = {
      start: "",
      end: "",
      firm: assignedFirmName,
      type: "",
      personalId: "",
      cardNo: "",
      departmentName: "",
    };
    setDraft(cleared);
    load(cleared);
  };

  const handlePreset = (key) => {
    const { start, end } = getDateRangePreset(key);
    const nextFilter = { ...draft, start, end };
    setDraft(nextFilter);
    load(nextFilter);
  };

  // Öncelik sırası (soldan sağa, en önemli -> en az önemli):
  // Tarih > Personel > İşlem Tipi > Tutar > Bakiye Tipi > Kalan > Önceki Bakiye
  // > Kart No > Departman > Firma > Otomat > Ürün > Slot No > İşlem No
  const columns = useMemo(
    () => [
      {
        field: "epoch",
        headerName: "Tarih",
        flex: 1,
        minWidth: 150,
        valueFormatter: (v) => formatEpoch(v),
      },
      { field: "nameSurname", headerName: "Personel", flex: 1, minWidth: 140 },
      {
        field: "transactionTypeId",
        headerName: "İşlem Tipi",
        minWidth: 175,
        flex: 1,
        valueGetter: (value) => transactionTypeLabel(value),
        renderCell: (params) => (
          <Badge tone={transactionTypeTone(params.row.transactionTypeId)}>
            {params.value}
          </Badge>
        ),
      },
      {
        field: "amount",
        headerName: "Tutar",
        width: 105,
        valueFormatter: (v) => formatKurus(v),
      },
      {
        field: "amountType",
        headerName: "Bakiye Tipi",
        width: 115,
        // valueGetter etiketi döndürür -> filtre/sıralama etikete göre çalışır.
        valueGetter: (value) => balanceTypeLabel(value),
        renderCell: (params) => (
          <Badge tone={balanceTypeTone(params.row.amountType)}>
            {params.value}
          </Badge>
        ),
      },
      {
        field: "postBalance",
        headerName: "Kalan",
        width: 105,
        valueFormatter: (v) => formatKurus(v),
      },
      {
        field: "previousBalance",
        headerName: "Önceki Bakiye",
        width: 130,
        valueFormatter: (v) => formatKurus(v),
      },
      { field: "cardNo", headerName: "Kart No", flex: 1, minWidth: 120 },
      { field: "departmentName", headerName: "Departman", flex: 1, minWidth: 110 },
      ...(canSelectFirm
        ? [{ field: "firmName", headerName: "Firma", flex: 1, minWidth: 120 }]
        : []),
      {
        field: "nodeName",
        headerName: "Otomat",
        flex: 1,
        minWidth: 120,
        valueFormatter: (value) => value || "",
      },
      { field: "productName", headerName: "Ürün", flex: 1, minWidth: 120 },
      { field: "productNo", headerName: "Slot No", width: 95 },
      { field: "transactionId", headerName: "İşlem No", flex: 1, minWidth: 220 },
    ],
    [canSelectFirm]
  );

  // Grid'de gösterilen biçim Excel'e de aynen gitsin.
  const excelValueTransformer = (value, column) => {
    if (column.field === "epoch") return formatEpoch(value);
    if (
      column.field === "previousBalance" ||
      column.field === "amount" ||
      column.field === "postBalance"
    ) {
      return formatKurus(value);
    }
    if (column.field === "transactionTypeId") return transactionTypeLabel(value);
    if (column.field === "amountType") return balanceTypeLabel(value);
    return value;
  };

  const handleExport = () => {
    const result = createAndDownloadExcel(
      rows,
      columns,
      "İşlemler",
      generateExcelFilename("Vendai_Digital_Pay_Islemler"),
      { valueTransformer: excelValueTransformer }
    );
    if (result.success) showSnackbar(`İndirildi: ${result.fileName}`, "success");
    else showSnackbar(result.error || "Dışa aktarılamadı", "error");
  };

  return (
    <>
      <Panel>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 2.25,
          }}
        >
          <DateRangePresetButtons onSelect={handlePreset} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              flexWrap: "wrap",
              ml: "auto",
            }}
          >
            <PrimaryButton startIcon={<FilterAltIcon />} onClick={apply}>
              Filtrele
            </PrimaryButton>
            <GhostButton
              startIcon={<CleaningServicesRoundedIcon />}
              onClick={clearFilters}
            >
              Filtreleri Temizle
            </GhostButton>
            <GhostButton
              startIcon={<RefreshIcon />}
              onClick={() => load(draft)}
              disabled={loading}
            >
              Yenile
            </GhostButton>
            <GhostButton startIcon={<FileDownloadIcon />} onClick={handleExport}>
              Dışa Aktar
            </GhostButton>
          </Box>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          <TextField
            select
            label="Personel"
            value={draft.personalId}
            onChange={(e) => setDraft({ ...draft, personalId: e.target.value })}
            size="small"
            fullWidth
          >
            <MenuItem value="">Tüm Personeller</MenuItem>
            {personOptions.map((person) => (
              <MenuItem key={person.idpersonal} value={person.idpersonal}>
                {[person.name, person.surname].filter(Boolean).join(" ") ||
                  person.regNumber}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Kart No"
            value={draft.cardNo}
            onChange={(e) => setDraft({ ...draft, cardNo: e.target.value })}
            size="small"
            fullWidth
          />
          {canSelectFirm && (
            <TextField
              select
              label="Firma"
              value={draft.firm}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  firm: e.target.value,
                  departmentName: "",
                })
              }
              size="small"
              fullWidth
            >
              <MenuItem value="">Tümü</MenuItem>
              {firms.map((f) => (
                <MenuItem key={f.idfirm} value={f.name}>
                  {f.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            label="Departman"
            value={draft.departmentName}
            onChange={(e) =>
              setDraft({ ...draft, departmentName: e.target.value })
            }
            size="small"
            fullWidth
          >
            <MenuItem value="">Tüm Departmanlar</MenuItem>
            {departmentOptions.map((department) => (
              <MenuItem key={department.iddepartment} value={department.name}>
                {department.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Başlangıç Tarihi"
            type="date"
            value={draft.start}
            onChange={(e) => setDraft({ ...draft, start: e.target.value })}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
          <TextField
            label="Bitiş Tarihi"
            type="date"
            value={draft.end}
            onChange={(e) => setDraft({ ...draft, end: e.target.value })}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
          <TextField
            select
            label="İşlem Tipi"
            value={draft.type}
            onChange={(e) => setDraft({ ...draft, type: e.target.value })}
            size="small"
            fullWidth
          >
            <MenuItem value="">Tüm İşlem Tipleri</MenuItem>
            {TRANSACTION_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <ListGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.idtransaction}
            loading={loading}
            height={520}
          />
        </Box>
      </Panel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
